
import { useEffect, useRef, useState } from 'react';

export default function Solution() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const solutions = [
    {
      title: 'Intelligent Client Acquisition',
      description:
        'AI-powered lead capture, qualification, and automated follow-up sequences that convert at scale.',
      icon: 'ri-user-search-line',
      image:
        'https://public.readdy.ai/ai/img_res/6dc2ad7e2e97ff3959eda0d9c0253a2a.jpg',
    },
    {
      title: 'Workflow Automation Infrastructure',
      description:
        'Custom CRM systems that streamline operations, intake, scheduling, and internal processes.',
      icon: 'ri-flow-chart',
      image:
        'https://readdy.ai/api/search-image?query=overhead%20cinematic%20shot%20of%20a%20sleek%20dark%20workspace%20with%20multiple%20connected%20screens%20showing%20workflow%20diagrams%20and%20automation%20pipelines%20in%20silver%20and%20white%20on%20black%20backgrounds%20premium%20technology%20setup%20with%20ambient%20lighting%20and%20clean%20geometric%20design%20minimal%20sophisticated%20atmosphere&width=800&height=500&seq=sol-apple-002&orientation=landscape',
    },
    {
      title: 'Revenue Optimization Systems',
      description:
        'Retention automations, reactivation flows, and performance dashboards that drive measurable ROI.',
      icon: 'ri-line-chart-line',
      image:
        'https://readdy.ai/api/search-image?query=dramatic%20cinematic%20visualization%20of%20ascending%20financial%20growth%20charts%20and%20performance%20dashboards%20rendered%20in%20silver%20metallic%20and%20white%20light%20on%20dark%20obsidian%20background%20premium%20data%20analytics%20display%20with%20clean%20typography%20and%20geometric%20precision%20volumetric%20lighting%20effects&width=800&height=500&seq=sol-apple-003&orientation=landscape',
    },
  ];

  return (
    <section ref={sectionRef} id="solutions" className="bg-[#0B0D10] py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-xs font-semibold tracking-[0.35em] text-[#8C9198] mb-5 uppercase">
            What We Build
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-b from-white to-[#8C9198] bg-clip-text text-transparent tracking-tight">
            Precision. Automation. Control.
          </h2>
        </div>

        <div className="space-y-8">
          {solutions.map((solution, index) => (
            <div
              key={index}
              className={`group relative grid grid-cols-1 md:grid-cols-2 gap-0 bg-[#111318] rounded-3xl overflow-hidden transition-all duration-700 hover:shadow-[0_0_80px_rgba(140,145,152,0.08)] cursor-pointer ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${(index + 1) * 200}ms` }}
            >
              {/* Image side */}
              <div
                className={`w-full h-80 md:h-[420px] overflow-hidden ${
                  index % 2 === 1 ? 'md:order-2' : ''
                }`}
              >
                <img
                  src={solution.image}
                  alt={solution.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>

              {/* Text side */}
              <div
                className={`flex flex-col justify-center p-12 md:p-16 ${
                  index % 2 === 1 ? 'md:order-1' : ''
                }`}
              >
                <div className="w-12 h-12 mb-6 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10">
                  <i className={`${solution.icon} text-2xl text-white/80`}></i>
                </div>

                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 tracking-tight">
                  {solution.title}
                </h3>

                <p className="text-base text-[#8C9198] leading-relaxed font-light max-w-md">
                  {solution.description}
                </p>

                <div className="mt-8 flex items-center gap-2 text-[#C5C9CF] group-hover:text-white transition-colors duration-300">
                  <span className="text-sm font-medium">Learn more</span>
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-base transition-transform duration-300 group-hover:translate-x-1"></i>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
